// @flow
import { createFormReducer } from 'redux-pure-form';
import { uniq } from 'lodash';

import type { Reducer } from 'redux';

import { normalizeData } from '../../configs/api-generators.js';

import {
	ADD_COMMENT,
	CANCEL_CHECKIN,
	CANCEL_CHECKOUT,
	CHANGE_STATUS,
	CHECK_EVENT_PARAMS,
	defaultDiscrepancyForm,
	defaultEventForm,
	defaultFilterForm,
	defaultFilterSettings,
	DELETE_DISCREPANCY,
	DISCREPANCY_ADD,
	DISCREPANCY_GET,
	EDIT_COMMENT,
	EVENT_ADD,
	EVENT_ADD_TO_WAITING,
	EVENT_CANCEL,
	EVENT_CHECKIN,
	EVENT_CHECKIN_START,
	EVENT_CHECKOUT,
	EVENT_CHECKOUT_START,
	EVENT_DELETE,
	EVENT_SAVE,
	EVENT_VIEW,
	GET_AIRCRAFT,
	GET_TIMEZONE,
	MOVE_EVENT,
	MOVE_EVENT_FAILED,
	MOVE_EVENT_FINISH,
	MOVE_EVENT_FINISH_FAILED,
	MOVE_EVENT_START,
	MOVE_EVENT_START_FAILED,
	SAVE_DISCREPANCY,
	SLICE_BREADCRUMBS
} from './calendar.consts.js';

import { applyTimezone, getNextEventsByResourse } from './calendar.model.js';

import type { FilterForm, FilterSettings } from './calendar.types.js';
import {
	CALCULATE_WEIGHT_AND_BALANCE_ACTION,
	SET_WB_EVENT_CONTEXT,
	WB_SAVE_DONE
} from './weight-and-balance/weight-and-balance.consts';

const filterSettingsHandler: Reducer<RegForm, Action> = createFormReducer(
	'filterSettings',
	defaultFilterSettings
);
export const filterSettings = (state: FilterSettings = defaultFilterSettings, action) =>
	filterSettingsHandler(state, action);

const filterFormHandler: Reducer<RegForm, Action> = createFormReducer(
	'filterForm',
	defaultFilterForm
);
export const filterForm = (state: FilterForm = defaultFilterForm, action) =>
	filterFormHandler(state, action);

const eventFormHandler: Reducer<RegForm, Action> = createFormReducer(
	'eventForm',
	defaultFilterForm
);
export const eventForm = (state: EventForm = defaultEventForm, action) => {
	switch (action.type) {
		case EVENT_ADD:
		case EVENT_SAVE:
			return defaultEventForm;
		case GET_TIMEZONE:
			return {
				...state,
				timezone: action.payload
			};
		case EVENT_ADD_TO_WAITING:
			return {
				...state,
				addToWaiting: action.payload.addToWaiting
			};
		case EVENT_VIEW:
			const timezone = action.payload.timeZone[action.payload.timeZoneID];
			return {
				...state,
				...action.payload,
				timezone,
				globalStartTime: applyTimezone(action.payload.startTime, timezone.offset),
				globalFinishTime: applyTimezone(action.payload.finishTime, timezone.offset),
				recurringEndDate: action.payload.recurringEndDate || '',
				recurringAction: 'save',
				resources: action.payload.resources.map(res => res.id)
			};
		case CHECK_EVENT_PARAMS:
			if (!state.pilotID && action.payload.pilotID) {
				return {
					...state,
					pilotID: action.payload.pilotID
				};
			}

			return state;
		default:
			return eventFormHandler(state, action);
	}
};

const timezoneHandler: Reducer<RegForm, Action> = createFormReducer('timezone', 0);
export const timezone = (state, action) => timezoneHandler(state, action);

export const groupedTimezones = (state = {}) => state;
export const timezones = (state = {}) => state;

export const medicalClass = (state = {}) => state;

export const userFilters = (state = {}) => state;
export const userFiltersList = (state = {}) => state;

export const organizations = (state = {}) => state;
export const organizationsList = (state = {}) => state;

export const locations = (state = {}) => state;
export const locationsList = (state = {}) => state;

export const pilots = (state = {}) => state;
export const pilotsList = (state = {}) => state;

export const instructors = (state = {}) => state;
export const instructorsList = (state = {}) => state;

export function resources(state = {}, action) {
	const { nextRes, nextResByEvent } =
		action && action.payload ? getNextEventsByResourse(action.payload) : {};

	switch (action.type) {
		case EVENT_ADD:
			const updatedResources = Object.keys(nextRes).reduce(
				(acc, resID) => ({
					...acc,
					[resID]: {
						...state[resID],
						events: uniq([...nextRes[resID], ...(state[resID] ? state[resID].events : [])])
					}
				}),
				{}
			);

			return {
				...state,
				...updatedResources
			};

		case EVENT_DELETE:
		case EVENT_CANCEL:
			const deletedEvents = action.payload.fromDays.map(item => item.event.id);
			return Object.keys(state).reduce(
				(acc, resID) => ({
					...acc,
					[resID]: {
						...state[resID],
						events: uniq([
							...state[resID].events.filter(eventID => deletedEvents.indexOf(eventID) === -1),
							...(nextRes[resID] || [])
						])
					}
				}),
				{}
			);

		case EVENT_SAVE:
			return Object.keys(state).reduce(
				(acc, resID) => ({
					...acc,
					[resID]: {
						...state[resID],
						events: uniq([
							...state[resID].events.filter(
								eventID =>
									(nextResByEvent[eventID] && nextResByEvent[eventID].indexOf(resID) > -1) ||
									!nextResByEvent[eventID]
							),
							...(nextRes[resID] || [])
						])
					}
				}),
				{}
			);

		case MOVE_EVENT:
			return Object.keys(state).reduce(
				(acc, resID) => ({
					...acc,
					[resID]: {
						...state[resID],
						events: uniq([
							...state[resID].events.filter(
								eventID =>
									!(eventID === action.payload.event.id && action.payload.oldResource === resID) &&
									((nextResByEvent[eventID] && nextResByEvent[eventID].indexOf(resID) > -1) ||
										!nextResByEvent[eventID])
							),
							...(nextRes[resID] || [])
						])
					}
				}),
				{}
			);
		default:
			return state;
	}
}

export const resourcesList = (state = {}) => state;

export const days = (state = {}) => state;
export const daysList = (state = {}) => state;

export const locationsSchedule = (state = {}) => state;

export const resourcesSchedule = (state = {}) => state;
export const instructorsAccessibility = (state = {}) => state;

const aircraftHandler: Reducer<RegForm, Action> = createFormReducer('aircraft', {});
export function aircraft(state = {}, action) {
	switch (action.type) {
		case GET_AIRCRAFT:
			return {
				...action.payload,
				discrepancyStatus: 'all'
			};
		case CHANGE_STATUS:
			return {
				...state,
				discrepancies: {
					...state.discrepancies,
					[action.payload.id]: {
						...state.discrepancies[action.payload.id],
						status: action.payload.status
					}
				}
			};
		case SAVE_DISCREPANCY:
			return {
				...state,
				discrepancies: {
					...state.discrepancies,
					[action.payload.discrepancy.id]: {
						...state.discrepancies[action.payload.discrepancy.id],
						...action.payload.discrepancy
					}
				}
			};
		case DISCREPANCY_ADD:
			return {
				...state,
				discrepancies: {
					...state.discrepancies,
					[action.payload.id]: action.payload
				},
				discrepanciesList: [...state.discrepanciesList, action.payload.id]
			};
		case DELETE_DISCREPANCY:
			if (!state.discrepanciesList) return state;
			const index = state.discrepanciesList.indexOf(action.payload.id);
			if (index === -1) return state;

			return {
				...state,
				discrepanciesList: [
					...state.discrepanciesList.slice(0, index),
					...state.discrepanciesList.slice(0, index + 1)
				]
			};
		default:
			return aircraftHandler(state, action);
	}
}

const checkoutFormHandler: Reducer<RegForm, Action> = createFormReducer('checkoutForm', {});
export function checkoutForm(state = {}, action) {
	switch (action.type) {
		case EVENT_CHECKOUT_START:
			return action.payload;
		case EVENT_CHECKOUT:
		case CANCEL_CHECKOUT:
			return {};
		default:
			return checkoutFormHandler(state, action);
	}
}

const checkinFormHandler: Reducer<RegForm, Action> = createFormReducer('checkinForm', {});
export function checkinForm(state = {}, action) {
	switch (action.type) {
		case EVENT_CHECKIN_START:
			return action.payload;
		case EVENT_CHECKIN:
		case CANCEL_CHECKIN:
			return {};
		default:
			return checkinFormHandler(state, action);
	}
}

const discrepancyFormHandler: Reducer<RegForm, Action> = createFormReducer('discrepancyForm', {});
export function discrepancyForm(state = defaultDiscrepancyForm, action) {
	switch (action.type) {
		case DISCREPANCY_ADD:
			return defaultDiscrepancyForm;
		default:
			return discrepancyFormHandler(state, action);
	}
}

const discrepancyHandler: Reducer<RegForm, Action> = createFormReducer('discrepancy', {});
export function discrepancy(state = {}, action) {
	switch (action.type) {
		case DISCREPANCY_GET:
			const { activities, activitiesList } = normalizeData(
				action.payload.activities,
				'activities',
				'activitiesList'
			);
			return {
				...action.payload.discrepancy,
				activities,
				activitiesList
			};
		case SAVE_DISCREPANCY:
			return {
				...action.payload.discrepancy,
				activities: {
					...state.activities,
					[action.payload.activities[0].id]: action.payload.activities[0]
				},
				activitiesList: [...(state.activitiesList || []), action.payload.activities[0].id]
			};
		case CHANGE_STATUS:
			return {
				...state,
				status: action.payload.status,
				activities: {
					...state.activities,
					[action.payload.activities[0].id]: action.payload.activities[0]
				},
				activitiesList: [...(state.activitiesList || []), action.payload.activities[0].id]
			};
		case EDIT_COMMENT:
			return {
				...state,
				disposition: action.payload.disposition
			};
		case ADD_COMMENT:
			return {
				...state,
				disposition: '',
				activities: {
					...state.activities,
					[action.payload.activities[0].id]: action.payload.activities[0]
				},
				activitiesList: [...(state.activitiesList || []), action.payload.activities[0].id]
			};
		case DELETE_DISCREPANCY:
			const index = state.activitiesList.indexOf(action.payload.id);
			if (index === -1) return state;
			return {
				...state,
				activitiesList: [
					...state.activitiesList.slice(0, index),
					...state.activitiesList.slice(0, index + 1)
				]
			};
		default:
			return discrepancyHandler(state, action);
	}
}

export const organizationsAccess = (state = {}) => state;

export const customFields = (state = {}) => state;
export const customFieldsList = (state = {}) => state;

export const activityTypes = (state = {}) => state;
export const activityTypesList = (state = {}) => state;
export const activeActivityTypesList = (state = {}) => state;

export const mechanics = (state = {}) => state;
export const mechanicsList = (state = {}) => state;

const userUIHandler: Reducer<RegForm, Action> = createFormReducer('userUI', {});
export const userUI = (state = { scrollY: 0 }, action) => userUIHandler(state, action);

export function eventModalData(state = {}, action) {
	switch (action.type) {
		case CHECK_EVENT_PARAMS:
			return action.payload;
		case EVENT_ADD:
			return {};
		case EVENT_SAVE:
			return {};
		case EVENT_VIEW:
			return {};
		default:
			return state;
	}
}

const URLHandler: Reducer<RegForm, Action> = createFormReducer('URL', {});
export function URL(state = {}, action) {
	switch (action.type) {
		case SLICE_BREADCRUMBS:
			if (!action.payload.num) {
				return {
					...state,
					eventBreadcrumbs: [state.eventBreadcrumbs[0]]
				};
			}
			return {
				...state,
				eventBreadcrumbs: [
					...state.eventBreadcrumbs.slice(0, state.eventBreadcrumbs.length - action.payload.num)
				]
			};
		default:
			return URLHandler(state, action);
	}
}

export function weightAndBalance(state = null, action: Action) {
	switch (action.type) {
		case CALCULATE_WEIGHT_AND_BALANCE_ACTION: {
			// TODO remove after  backend fix
			const safeAction = { ...action };

			if (typeof safeAction.payload.passengers === 'string') {
				safeAction.payload.passengers = JSON.parse(`${safeAction.payload.passengers}`);
			}

			return {
				...state,
				...safeAction.payload,
				warning: safeAction.payload.warning
			};
		}
		case WB_SAVE_DONE:
			// Month-view save signal: stamps savedAt so EventViewModal can subscribe
			// and trigger a detail refetch + grid refresh without calling legacy getEvent().
			return { ...state, savedAt: Date.now() };
		default:
			return state;
	}
}

export function wbEventContext(state = null, action: Action) {
	switch (action.type) {
		case SET_WB_EVENT_CONTEXT:
			return action.payload;
		case EVENT_VIEW:
			return null;
		default:
			return state;
	}
}

const draggedEventHandler: Reducer<RegForm, Action> = createFormReducer('draggedEvent', {});
export function draggedEvent(state: DraggedEvent = {}, action: Action) {
	switch (action.type) {
		case MOVE_EVENT_START:
		case MOVE_EVENT_START_FAILED:
		case MOVE_EVENT_FINISH:
		case MOVE_EVENT_FINISH_FAILED:
		case MOVE_EVENT:
		case MOVE_EVENT_FAILED:
		case EVENT_DELETE:
		case EVENT_CANCEL:
		case EVENT_SAVE:
		case EVENT_VIEW:
		case EVENT_CHECKIN:
		case EVENT_CHECKOUT:
			return {};
		default:
			return draggedEventHandler(state, action);
	}
}
